import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { ChevronDownIcon, DocumentTextIcon, EyeIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';
import type { ReportContent, ReportEditorState } from '../../types/admin.types';

export interface ReportEditorProps {
  versionId?: string;
  content?: any;
  onChange?: (data: any) => void;
}

const ReportEditor: React.FC<ReportEditorProps> = ({ versionId, content, onChange }) => {
  const [profiles, setProfiles] = useState<string[]>([]);
  const [profileNames, setProfileNames] = useState<Record<string, string>>({});
  const [state, setState] = useState<ReportEditorState>({
    selectedProfileId: null,
    currentContent: '',
    isLoading: false,
    isSaving: false,
    hasUnsavedChanges: false,
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  // Extract profiles from content.report_contents.perfis (same source as ProfileEditor)
  useEffect(() => {
    console.log('🔄 ReportEditor: Content changed, reloading profiles...', { 
      content: !!content,
      timestamp: Date.now()
    });
    
    if (content?.report_contents?.perfis) {
      const availableProfiles = Object.keys(content.report_contents.perfis);
      const names: Record<string, string> = {};
      
      console.log('🔍 ReportEditor: Raw report_contents.perfis', content.report_contents.perfis);
      
      availableProfiles.forEach(id => {
        const profile = content.report_contents.perfis[id];
        names[id] = profile?.nome || id;
        console.log(`🔍 Profile ${id}:`, { nome: profile?.nome });
      });
      
      setProfiles(availableProfiles);
      setProfileNames(names);
      console.log('📊 ReportEditor: Final loaded profiles', names);
    } else {
      console.warn('⚠️ ReportEditor: No report_contents.perfis found in content');
      setProfiles([]);
      setProfileNames({});
    }
  }, [content]);

  // Load report content when profile is selected
  useEffect(() => {
    if (state.selectedProfileId) {
      loadReportContent(state.selectedProfileId);
    }
  }, [state.selectedProfileId]);

  const loadReportContent = async (profileId: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: undefined }));
    try {
      const reportContent = await apiService.getProfileReport(profileId);
      setState(prev => ({
        ...prev,
        currentContent: reportContent.conteudo || '',
        isLoading: false,
        hasUnsavedChanges: false,
      }));
    } catch (error: any) {
      // If report doesn't exist yet, start with empty content
      if (error.response?.status === 404) {
        setState(prev => ({
          ...prev,
          currentContent: `# Relatório de Perfil - ${getProfileDisplayName(profileId)}\n\nConteúdo a ser definido...`,
          isLoading: false,
          hasUnsavedChanges: false,
        }));
      } else {
        setState(prev => ({
          ...prev,
          error: `Erro ao carregar relatório: ${error.message}`,
          isLoading: false,
        }));
      }
    }
  };

  const handleProfileSelect = (profileId: string) => {
    if (state.hasUnsavedChanges) {
      const confirmChange = window.confirm(
        'Você tem alterações não salvas. Deseja continuar sem salvar?'
      );
      if (!confirmChange) {
        return;
      }
    }
    
    setState(prev => ({
      ...prev,
      selectedProfileId: profileId,
      hasUnsavedChanges: false,
    }));
    setShowProfileDropdown(false);
  };

  const handleContentChange = (value?: string) => {
    setState(prev => ({
      ...prev,
      currentContent: value || '',
      hasUnsavedChanges: true,
    }));
  };

  const handleSave = async () => {
    if (!state.selectedProfileId) return;

    setState(prev => ({ ...prev, isSaving: true, error: undefined }));
    try {
      const reportContent: ReportContent = {
        formato: 'markdown',
        conteudo: state.currentContent,
        editor: 'admin', // TODO: Get from auth context
        ultima_edicao: new Date().toISOString(),
      };

      await apiService.updateProfileReport(state.selectedProfileId, reportContent);
      
      setState(prev => ({
        ...prev,
        isSaving: false,
        hasUnsavedChanges: false,
        lastSaved: new Date(),
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: `Erro ao salvar: ${error.message}`,
        isSaving: false,
      }));
    }
  };

  const handlePreview = async () => {
    if (!state.selectedProfileId || !state.currentContent) return;

    try {
      const response = await apiService.previewProfileReport(
        state.selectedProfileId,
        state.currentContent
      );
      setPreviewHtml(response.html);
      setShowPreview(true);
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: `Erro no preview: ${error.message}`,
      }));
    }
  };

  const getProfileDisplayName = (profileId: string): string => {
    // Get profile name from cached profileNames state
    if (profileNames[profileId]) {
      return profileNames[profileId];
    }
    // Fallback to formatted profileId
    return profileId.charAt(0).toUpperCase() + profileId.slice(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Editor de Relatórios</h2>
        </div>
        
        {state.lastSaved && (
          <div className="text-sm text-gray-500">
            Salvo em {state.lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Profile Selector */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Selecionar Perfil
        </label>
        <div className="relative">
          <button
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <div className="flex items-center justify-between">
              <span className={state.selectedProfileId ? 'text-gray-900' : 'text-gray-500'}>
                {state.selectedProfileId 
                  ? getProfileDisplayName(state.selectedProfileId)
                  : 'Selecione um perfil para editar'
                }
              </span>
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </div>
          </button>

          {showProfileDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
              {profiles.map((profileId) => (
                <button
                  key={profileId}
                  onClick={() => handleProfileSelect(profileId)}
                  className={`w-full text-left px-4 py-2 hover:bg-gray-100 ${
                    state.selectedProfileId === profileId ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                  }`}
                >
                  {getProfileDisplayName(profileId)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {state.error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{state.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Editor Section */}
      {state.selectedProfileId && (
        <div className="space-y-4">
          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={!state.hasUnsavedChanges || state.isSaving}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                state.hasUnsavedChanges && !state.isSaving
                  ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              {state.isSaving ? 'Salvando...' : 'Salvar'}
            </button>

            <button
              onClick={handlePreview}
              disabled={!state.currentContent}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              Preview
            </button>

            {state.hasUnsavedChanges && (
              <span className="text-sm text-amber-600 font-medium">
                • Alterações não salvas
              </span>
            )}
          </div>

          {/* Markdown Editor */}
          <div className="border border-gray-300 rounded-md overflow-hidden">
            {state.isLoading ? (
              <div className="h-96 flex items-center justify-center bg-gray-50">
                <div className="text-gray-500">Carregando conteúdo...</div>
              </div>
            ) : (
              <MDEditor
                value={state.currentContent}
                onChange={handleContentChange}
                height={600}
                preview="live"
                visibleDragBar={false}
                data-color-mode="light"
              />
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Preview do Relatório</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportEditor;
